import map from 'lodash/map'
import { useFilter } from './Filters/useFilter'
import { useFilterOnBoarding } from './onboard/useFilterOnBoarding'
import { useProductMetadata } from '@ztechapps/product.webui'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import React, { useContext as useContextCore } from 'react'

import { useProductViewLayout } from './useProductViewLayout'
import { ViewLayoutType } from '../../../config/types'
import toNumber from 'lodash/toNumber'
import { useSearchParams } from 'react-router-dom'

export type ProviderProps = {
  children: React.ReactNode
}

export type ContextType = Omit<ProviderProps, 'children'> &
  ReturnType<typeof useFilter> & {
    showMobileFilter: boolean
    setShowMobileFilter: Dispatch<SetStateAction<boolean>>
    onBoarding: ReturnType<typeof useFilterOnBoarding>
    showOnBoarding: boolean
    metadataSchema: Record<any, any>
    viewLayout: ViewLayoutType
    setViewLayout: Dispatch<SetStateAction<ViewLayoutType>>
    productsCount: number
    setProductsCount: Dispatch<SetStateAction<number>>
    limit: number
    skip: number
    sort: Record<any, any>
    filter: Record<any, any>
    search: string
  }

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider = ({ children }: { children: ReactNode }) => {
  const { setViewLayout, viewLayout } = useProductViewLayout()
  const [searchParams] = useSearchParams()

  const search = searchParams.get('search')
  const skipParam = searchParams.get('skip')
  //guard against NaN/negative/non-finite URL input — Paging.tsx throws RangeError on NaN
  const skipRaw = skipParam ? toNumber(skipParam) : 1
  const skip = Number.isFinite(skipRaw) && skipRaw >= 1 ? skipRaw : 1

  const [showMobileFilter, setShowMobileFilter] = React.useState(false)
  const { metadataSchema } = useProductMetadata()
  const [productsCount, setProductsCount] = React.useState(0)

  const limit = 10
  let sort: any = { code: -1 }

  const { filter: filterProxy, defaultFilter, ...filterRest } = useFilter()

  let filter: any = filterProxy

  if (search && viewLayout === ViewLayoutType.list) {
    const searchTerms = search.split(' ').filter((s) => s.trim() !== '')
    const finalSearch = map(searchTerms, (item) => `"${item}"`).join(' ')

    filter = {
      $text: { $search: finalSearch },
      ...defaultFilter,
    }
    sort = { searchScore: { $meta: 'textScore' } }
  }

  //the state fof onboarding
  const showOnBoarding = true

  const onBoarding = useFilterOnBoarding({ enabled: showOnBoarding })

  return (
    <CoreProvider
      value={{
        ...filterRest,
        defaultFilter,
        filter,
        sort,
        skip,
        limit,
        search,
        showMobileFilter,
        setShowMobileFilter,
        onBoarding,
        showOnBoarding,
        metadataSchema,
        viewLayout,
        setViewLayout,
        productsCount,
        setProductsCount,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useContext = () => useContextCore(context)
